import type { DataLayerEvent } from '../../../types'
import { pushEventToDataLayer } from '../../index'
import {
  type Ga4ValidationIssue,
  getValidationErrors,
} from '../validation/get-validation-errors'

const GA4_EVENT_VALIDATION_OWNERS = ['bot-squad-content-front']

function formatIssueValue(
  value: unknown,
  seen = new WeakSet<object>(),
): string {
  if (typeof value === 'string') return `"${value}"`

  if (typeof value === 'object' && value !== null) {
    if (seen.has(value)) return '[Circular]'
    seen.add(value)

    try {
      if (Array.isArray(value)) {
        return value.map((item) => formatIssueValue(item, seen)).join(', ')
      }

      const entries = Object.entries(value)

      return entries.length > 0
        ? entries
            .map(([key, item]) => `${key}: ${formatIssueValue(item, seen)}`)
            .join(', ')
        : '(empty object)'
    } finally {
      seen.delete(value)
    }
  }

  return String(value)
}

function formatValidationIssues(issues: Ga4ValidationIssue[]): string {
  return issues
    .map((issue) => {
      const field = issue.field || '<root>'
      const message =
        issue.expected !== undefined && 'received' in issue
          ? `expected ${issue.expected}, received ${formatIssueValue(issue.received)}`
          : issue.message

      return `${field} [${issue.error_type}]: ${message}`
    })
    .join('; ')
}

/** Dispatches through the shared GTM data-layer handler and validates asynchronously. */
export function pushEvent(event: DataLayerEvent): void {
  if (!import.meta.client) return

  pushEventToDataLayer(event)

  window.setTimeout(() => {
    try {
      const validationErrors = getValidationErrors(event)

      if (validationErrors) {
        const itemId = (
          event.ecommerce as
            | { items?: Array<{ item_id?: unknown }> }
            | undefined
        )?.items?.[0]?.item_id

        window.logger.error(
          `[GA4] Invalid ${validationErrors.eventName} payload`,
          {
            issues: formatValidationIssues(validationErrors.issues),
            item_id: itemId,
            owners: GA4_EVENT_VALIDATION_OWNERS,
          },
        )
      }
    } catch {
      // fails silently
    }
  }, 0)
}
